import * as React from 'react';
import * as ReactDOM from 'react-dom';
import styled, { keyframes, createGlobalStyle } from 'styled-components';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';

const ModalInfo = ({
  title = 'Success',
  timeToClose = 5000,
  onClose,
  isError = false,
  errorMsg = 'Error: Unable to perform action',
  successMsg = 'Your changes have been saved.',
  hideSubtitle = false,
  message,
  primaryLabel,
  onPrimary,
  secondaryLabel,
  onSecondary,
}: Props) => {
  const overlayRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    let timer;

    if (timeToClose && !timer) {
      timer = setTimeout(() => {
        onClose();
      }, timeToClose);
    }

    return () => timeToClose && clearTimeout(timer);
  }, [timeToClose]);

  // ModalInfo portals to <body>, so it sits OUTSIDE any underlying
  // ModalDefaultDraggable, whose ClickAwayListener closes the modal on a
  // mousedown anywhere outside itself. Without this, pressing inside this popup
  // (e.g. the Dismiss button) is read as a click "away" and closes the modal
  // behind it. Stop the native mousedown/touch from reaching document — the
  // click still fires, so the overlay/Dismiss onClose handlers keep working.
  React.useEffect(() => {
    const node = overlayRef.current;
    if (!node) return undefined;
    const stop = (e: Event) => e.stopPropagation();
    node.addEventListener('mousedown', stop);
    node.addEventListener('touchstart', stop);
    node.addEventListener('touchend', stop);
    return () => {
      node.removeEventListener('mousedown', stop);
      node.removeEventListener('touchstart', stop);
      node.removeEventListener('touchend', stop);
    };
  }, []);

  // In error mode the heading is normally the error text, but when an explicit
  // `message` body is supplied we promote `title` to the heading instead.
  let heading = title;
  if (isError && !message) {
    heading = errorMsg;
  }

  return ReactDOM.createPortal(
    <Overlay ref={overlayRef} onClick={onClose}>
      <GlobalStyles />
      <Card $isError={isError} onClick={e => e.stopPropagation()}>
        <IconWrap $isError={isError}>
          {isError ? (
            <ErrorOutlineIcon />
          ) : (
            <CheckCircleOutlineIcon />
          )}
        </IconWrap>
        <Title $isError={isError}>{heading}</Title>
        {message ? (
          <Subtitle>{message}</Subtitle>
        ) : (
          !hideSubtitle && (
            <Subtitle>
              {isError ? 'Please try again or contact support.' : successMsg}
            </Subtitle>
          )
        )}
        <ButtonRow>
          <CloseBtn onClick={onPrimary || onClose}>
            {primaryLabel || (isError ? 'Dismiss' : 'OK')}
          </CloseBtn>
          {onSecondary && (
            <SecondaryBtn onClick={onSecondary}>{secondaryLabel}</SecondaryBtn>
          )}
        </ButtonRow>
        <ProgressBar $duration={timeToClose} $isError={isError} />
      </Card>
    </Overlay>,
    document.body,
  );
};

const GlobalStyles = createGlobalStyle`
  body {
    overflow: hidden;
  }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: scale(0.9) translateY(10px); }
  to { opacity: 1; transform: scale(1) translateY(0); }
`;

const shrink = keyframes`
  from { width: 100%; }
  to { width: 0%; }
`;

const Overlay = styled.div`
  position: fixed;
  z-index: 1400;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(19, 23, 32, 0.35);
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
`;

const Card = styled.div<{ $isError: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-8) var(--space-10) var(--space-6);
  background: var(--neutral-0);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  border: 1px solid var(--neutral-200);
  min-width: 320px;
  max-width: 440px;
  text-align: center;
  animation: ${fadeIn} 0.25s var(--ease-out);
  overflow: hidden;
`;

const IconWrap = styled.div<{ $isError: boolean }>`
  width: 64px;
  height: 64px;
  border-radius: var(--radius-full);
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $isError }) =>
    $isError ? 'var(--danger-50)' : 'var(--success-50)'};

  svg {
    width: 36px;
    height: 36px;
    color: ${({ $isError }) =>
      $isError ? 'var(--danger-500)' : 'var(--success-500)'};
  }
`;

const Title = styled.div<{ $isError: boolean }>`
  font-size: var(--fs-xl);
  font-weight: var(--fw-semibold);
  color: ${({ $isError }) =>
    $isError ? 'var(--danger-700)' : 'var(--neutral-900)'};
  line-height: var(--lh-snug);
`;

const Subtitle = styled.div`
  font-size: var(--fs-base);
  color: var(--neutral-500);
  line-height: var(--lh-normal);
`;

const ButtonRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-3);
  margin-top: var(--space-2);
`;

const CloseBtn = styled.button`
  padding: var(--space-2) var(--space-8);
  border: 0;
  border-radius: var(--radius-md);
  background: var(--primary-600);
  color: var(--neutral-0);
  font-size: var(--fs-base);
  font-weight: var(--fw-semibold);
  font-family: inherit;
  cursor: pointer;
  transition: background-color var(--motion-fast) var(--ease-standard);

  &:hover {
    background: var(--primary-700);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

const SecondaryBtn = styled.button`
  padding: var(--space-2) var(--space-8);
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  background: var(--neutral-0);
  color: var(--neutral-700);
  font-size: var(--fs-base);
  font-weight: var(--fw-semibold);
  font-family: inherit;
  cursor: pointer;
  transition: background-color var(--motion-fast) var(--ease-standard), border-color var(--motion-fast) var(--ease-standard);

  &:hover {
    background: var(--neutral-50);
    border-color: var(--neutral-400);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

const ProgressBar = styled.div<{ $duration: number; $isError: boolean }>`
  position: absolute;
  bottom: 0;
  left: 0;
  height: 3px;
  background: ${({ $isError }) =>
    $isError ? 'var(--danger-500)' : 'var(--success-500)'};
  animation: ${shrink} ${({ $duration }) => $duration}ms linear forwards;
`;

interface Props {
  title?: string;
  timeToClose?: number;
  isError?: boolean;
  onClose: () => void;
  errorMsg?: string;
  successMsg?: string;
  hideSubtitle?: boolean;
  /**
   * Optional descriptive body shown below the heading. When provided in error
   * mode, `title` is used as the heading (instead of `errorMsg`) and this text
   * fills the body — letting a popup show both a title and a custom message.
   */
  message?: React.ReactNode;
  /** Overrides the primary button's label (default: 'Dismiss' / 'OK'). */
  primaryLabel?: string;
  /** Overrides the primary button's handler (default: onClose). */
  onPrimary?: () => void;
  /** Label for an optional second button, e.g. for a Yes/No confirmation. */
  secondaryLabel?: string;
  /** Handler for the optional second button. Renders it only when provided. */
  onSecondary?: () => void;
}

export default ModalInfo;
